<template>
    <a
        :href="product.url"
        class="ProductCard"
        :class="{
            'ProductCard--overlay' : product.displayStartDate,
            'ProductCard--single-date' : oneDayEvent
        }"
    >
        <div class="ProductCard__image">
            <BaseImage
                :placeholder="product.placeholder"
                :srcset="product.image"
                :sizes="sizes"
                :ratio="product.ratio"
            >
                <div class="product-location-tag" v-if="product.location">
                    <BaseTag :title="product.location" style="margin-bottom: 0;" />
                </div>
            </BaseImage>
            <div class="ProductCard__date text--white" v-if="product.displayStartDate">
                <span class="ProductCard__day">{{ product.displayStartDate.day }}</span>
                <span class="ProductCard__month">{{ product.displayStartDate.month }}</span>
                <div v-if="!oneDayEvent" class="ProductCard__end-date">
                    <span class="ProductCard__divider">⬤</span>
                    <span class="ProductCard__day">{{ product.displayEndDate.day }}</span>
                    <span class="ProductCard__month">{{ product.displayEndDate.month }}</span>
                </div>
            </div>
            <BaseTag
                v-if="product.region"
                :title="product.region"
                class="mr-min"
            >
            </BaseTag>
        </div>
        <div class="ProductCard__content">
            <span
                v-if="subcategory"
                class="ProductCard__subheading uppercase text--grey-dark"
            >
                {{ subcategory }}
            </span>
            <BaseHeading
                :heading="product.heading || product.title"
                :tag="tag"
            />
            <BaseText
                :text="productDesc"
            />
            <ul
                v-if="showFeatures"
                class="ProductCard__features"
            >
                <li
                    v-for="(feature, index) in product.features"
                    :key="index"
                    class="mr-small"
                >
                    <BaseIcon
                        :icon="feature"
                    />
                </li>
            </ul>
        </div>
    </a>
</template>
<script>
import { truncate } from 'lodash';

export default {
    name: 'ProductCard',
    props: {
        product: {
            placeholder: {
                type: String
            },
            image: {
                type: String
            },
            title: {
                type: String
            },
            categories: {
                type: String,
                default: null,
            },
            description: {
                type: String,
                default: '',
            },
            location: {
                type: String,
            },
            url: {
                type: String,
            }
        },
        tag: {
            default: 'h6',
        },
        hideDesc: {
            type: Boolean,
            default: false
        },
        truncateText: {
            type: Boolean,
            default: true
        },
        showFeatures: {
            type: Boolean,
            default: false
        },
        subcategory: {
            type: String,
            default: null
        },
        sizes: {
            type: String,
            default: undefined
        }
    },
    computed: {
        oneDayEvent() {
            return this.product.displayStartDate && JSON.stringify(this.product.displayStartDate) === JSON.stringify(this.product.displayEndDate);
        },
        productDesc() {
            let text
            if (this.truncateText) {
                text = this.truncatedDescription
            } else {
                text = this.product.description ? `<p>${this.product.description}</p>` : null
            }

            return this.hideDesc === false ? text : null;
        },

        truncatedDescription() {
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = this.product.description;

            const truncated = truncate(tempDiv.innerText, {
                length: 155,
                separator: /,? +/,
                omission: '...'
            });

            return `<p>${truncated}</p>`;
        }
    }
}
</script>
<style lang="scss" scoped>
    .BaseImage {
        position: relative;
        margin-bottom: getSpacing('spacer');

        &:after {
            content: '';
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
            opacity: 0.05;
            background: getColor('black');
            transition: opacity 1200ms cubic-bezier(0.23, 1, 0.32, 1);

            .ProductCard:hover & {
                opacity: 0.35;
            }
        }

        .ProductCard--flex & {
            @include media-query(smart) {
                width: 100%;
                padding-bottom: 0!important;
                height: 80px;
            }

            @media only screen and (max-width: 420px) {
                height: 50px;
            }
        }
    }

    .ProductCard--flex > .ProductCard__image {
        @include media-query(smart) {
            width: 25%;
            margin-right: 0.75rem;
        }
    }

    .ProductCard--flex > .ProductCard__content {
        @include media-query(smart) {
            width: 75%;
        }
    }

    .ProductCard--single-date {
        .ProductCard__day {
            font-size: 4rem;
        }

        .ProductCard__month {
            font-size: 1.3rem;
        }

        .ProductCard__divider {
            font-size: 8px;
        }
    }

    .ProductCard--overlay {
        .ProductCard__image:after {
            content: '';
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
            background: getColor('black');
            opacity: 0.15;
            transition: all ease-out 0.456s;
        }
    }

    .BaseText {
        margin-top: getSpacing(small);
    }

    .ProductCard__image {
        position: relative;
    }

    .ProductCard__date {
        display: flex;
        position: absolute;
        left: getSpacing('medium');
        top: getSpacing('medium');
        z-index: 4;
    }

    .BaseTag {
        position: absolute;
        left: getSpacing('medium');
        bottom: getSpacing('medium');
        margin-bottom: 0;
        z-index: 2;
    }

    .ProductCard__subheading {
        font-size: 0.75rem;
        margin-right: 0.25rem;
        margin-bottom: 0.3rem;
    }

    .ProductCard__features {
        display: flex;
        margin-top: getSpacing(base);
        align-items: center;
        color: getColor('sand');
    }

    ::v-deep h6 {

        @include media-query(palm) {
            font-size: 1.5rem!important;
        }
    }

    .ProductCard__day {
        font-size: 2.8rem;
        line-height: 0;
        letter-spacing: -1px;
        font-family: "NationalBlack", Helvetica, sans-serif;
    }

    .ProductCard__month {
        font-size: 0.8rem;
        line-height: 0;
        vertical-align: top;
        text-transform: uppercase;
        letter-spacing: -1px;
        font-family: "NationalBlack", Helvetica, sans-serif;
    }

    .ProductCard__divider {
        font-size: 6px;
        vertical-align: text-top;
        margin-left: 10px;
        margin-right: 10px;
    }

    .ProductCard__end-date {
        display: flex;
    }

    .product-location-tag {
        position: absolute;
        bottom: 1rem;
        left: 1rem;

        .BaseTag {
            position: static;
        }

        @include media-query(smart) {
            display: none;
        }
    }
</style>
